import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Bell, CalendarDays, Loader2, Video } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCalendarMeetingReminder } from '@/hooks/useCalendarMeetingReminder';
import { useActiveAppUsersForSelect } from '@/hooks/useAppUsers';
import { useVideoRingNotificationsContext } from '@/contexts/VideoRingNotificationsContext';
import { ensureMeetingForEvent } from '@/lib/meetings/ensureMeetingForEvent';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

export function CalendarMeetingReminderModal() {
  const { reminder, dismiss } = useCalendarMeetingReminder();
  const { users } = useActiveAppUsersForSelect();
  const { openDailyCall } = useVideoRingNotificationsContext();
  const [joiningRoom, setJoiningRoom] = useState(false);

  if (!reminder) return null;

  const { event, phase } = reminder;

  const startTime = format(parseISO(event.start_at), 'HH:mm');
  const startDate = format(parseISO(event.start_at), "EEEE, d 'de' MMMM", { locale: ptBR });

  const getUserName = (id: string) => {
    const u = users.find((x) => x.id === id);
    if (!u) return null;
    return (
      u.display_name ||
      [u.first_name, u.last_name].filter(Boolean).join(' ') ||
      u.email ||
      null
    );
  };

  const attendeeNames = (event.attendee_ids ?? [])
    .map(getUserName)
    .filter(Boolean) as string[];

  const heading = phase === '5min' ? 'Reunião em 5 minutos' : 'Reunião começando agora!';

  const goToAgenda = () => {
    window.location.href = '/agenda';
    dismiss();
  };

  const handleJoin = async () => {
    setJoiningRoom(true);
    try {
      const result = await ensureMeetingForEvent({
        calendar_event_id: event.id,
        daily_room_url: event.daily_room_url,
        daily_room_name: event.daily_room_name,
        contact_id: event.contact_id,
        deal_id: event.deal_id,
        end_at: event.end_at,
        attendee_ids: event.attendee_ids || [],
      });

      if (result.consultant_token) {
        openDailyCall({
          roomUrl: result.room_url,
          token: result.consultant_token,
          callId: '',
          meetingId: result.meeting_id,
          clientJoinUrl: result.client_join_url,
        });
        dismiss();
      } else {
        console.warn('[CalendarMeetingReminderModal] No host token found, opening URL directly');
        window.open(result.room_url, '_blank');
        dismiss();
      }
    } catch (err: any) {
      console.error('[CalendarMeetingReminderModal] join failed:', err);
      toast.error('Erro ao entrar na sala. Tente novamente.');
    } finally {
      setJoiningRoom(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) dismiss(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className={`w-5 h-5 ${phase === 'exact' ? 'text-destructive animate-bounce' : 'text-amber-500'}`} />
            {heading}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-lg font-semibold">{event.title}</p>
          <p className="text-sm text-muted-foreground capitalize">
            {startDate} às {startTime}
          </p>
          {attendeeNames.length > 0 && (
            <div className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Participantes: </span>
              {attendeeNames.join(', ')}
            </div>
          )}
          {event.description && (
            <p className="text-sm text-muted-foreground border-l-2 pl-3">{event.description}</p>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" size="sm" onClick={dismiss} disabled={joiningRoom}>
            Dispensar
          </Button>

          <Button variant="outline" onClick={goToAgenda} disabled={joiningRoom}>
            <CalendarDays className="w-4 h-4 mr-2" />
            Ver na Agenda
          </Button>

          <Button onClick={handleJoin} disabled={joiningRoom}>
            {joiningRoom ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Video className="w-4 h-4 mr-2" />
            )}
            Ingressar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
